import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Loader2 } from 'lucide-react';
import api from '../api';
import { AssignRecertModal, toUS } from './AssignRecertModal';
import { ReviewRecertModal } from './ReviewRecertModal';

const badge = (d) => (d < 0 ? ['Overdue', 'bg-red-600 text-white'] : d <= 2 ? [d === 0 ? 'Today' : `${d}d left`, 'bg-red-100 text-red-700']
  : d <= 5 ? [`${d}d left`, 'bg-amber-100 text-amber-800'] : [`${d}d left`, 'bg-gray-100 text-gray-700']);

export const RecertDueCard = () => {
  const [items, setItems] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get('/recert/upcoming?days=7').then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  const changed = () => { load(); window.dispatchEvent(new Event('recert-changed')); };
  useEffect(() => { load(); }, []);

  const role = (JSON.parse(localStorage.getItem('ivory_user') || '{}').role || '');
  if (items?.length === 0 && /nurse|\bRN\b|\bLPN\b|\bLVN\b/i.test(role)) return null;
  return (
    <div className="bg-white rounded border border-gray-200 shadow-sm" data-testid="recert-due-card">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 rounded-t">
        <h3 className="font-semibold text-[#d4af37] flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Recertifications Due This Week</h3>
        {items && <span className="text-xs text-white/80" data-testid="recert-due-count">{items.length} patient(s)</span>}
      </div>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin m-4" /> : items.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500" data-testid="recert-due-empty">No 485 certification periods end in the next 7 days.</p>
      ) : (
        <ul className="divide-y">
          {items.map(it => {
            const [label, cls] = badge(it.daysLeft);
            return (
              <li key={it.patientId} data-testid={`recert-due-${it.patientId}`} className="flex items-center gap-3 px-4 py-2 hover:bg-amber-50 transition-colors">
                <button onClick={() => navigate(`/dashboard/patients/demographics/${it.patientId}`)} className="flex-1 text-left font-medium text-gray-800">
                  {it.patientName}
                </button>
                <span className="text-sm text-gray-600 hidden sm:inline">Ends {it.certPeriodEnd}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
                {it.assignment?.reviewStatus === 'approved' ? (
                  <button onClick={() => navigate(`/dashboard/clinical/poc-485/edit?id=${it.assignment.draft485Id}`)} data-testid={`recert-draft485-${it.patientId}`}
                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-600 text-white hover:bg-green-700" title={`Approved by ${it.assignment.reviewedBy}`}>
                    Approved, open draft 485
                  </button>
                ) : it.assignment?.completedAt ? (
                  <button onClick={() => setReviewing(it)} title={`Completed by ${it.assignment.completedBy}`} data-testid={`recert-done-${it.patientId}`}
                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500 text-gray-900 hover:bg-amber-400">
                    Done, {it.assignment.staffName}. Review
                  </button>
                ) : it.assignment?.reviewStatus === 'changes_requested' ? (
                  <button onClick={() => setAssigning(it)} className="text-xs text-amber-700 hover:underline text-right" title={it.assignment.reviewNote} data-testid={`recert-changes-${it.patientId}`}>
                    Changes requested, {it.assignment.staffName}
                  </button>
                ) : it.assignment ? (
                  <button onClick={() => setAssigning(it)} className="text-xs text-green-700 hover:underline text-right" data-testid={`recert-assigned-${it.patientId}`}>
                    Assigned: {it.assignment.staffName}, {toUS(it.assignment.visitDate)}
                  </button>
                ) : (
                  <button onClick={() => setAssigning(it)} data-testid={`recert-assign-btn-${it.patientId}`}
                    className="px-3 py-0.5 rounded-full text-xs font-semibold bg-gray-900 text-[#d4af37] hover:bg-gray-800">
                    Assign Nurse
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {assigning && <AssignRecertModal item={assigning} onClose={() => setAssigning(null)} onAssigned={changed} />}
      {reviewing && <ReviewRecertModal item={reviewing} onClose={() => setReviewing(null)} onReviewed={changed} />}
    </div>
  );
};
